import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, NgModule, PLATFORM_ID } from '@angular/core';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import * as i1 from '@ngrx/store';
import { INIT, createAction, props, createFeature, createReducer, on, createSelector, StoreModule, META_REDUCERS } from '@ngrx/store';

const REHYDRATE_ROOT_CONFIG = new InjectionToken('ngrxUniversalHydrateRootConfig');
const REHYDRATE_TRANSFER_STATE = makeStateKey('ngrxUniversalStatesTransferred');
const FEATURE_STORES = new InjectionToken('ngrxUniversalHydrateFeatureState');

const defaultRehydrationRootConfig = {
    stores: undefined,
    disableWarnings: false,
    mergeStrategy: "mergeOver" /* MergeStrategy.MERGE_OVER */,
};
function merge(over, under) {
    return Object.entries(over).reduce((prev, [key, value]) => (Object.assign(Object.assign({}, prev), { [key]: value })), under);
}
function mergeStates(initial, transfer, mergeStrategy) {
    switch (mergeStrategy) {
        case "overwrite" /* MergeStrategy.OVERWRITE */:
            return transfer;
        case "mergeOver" /* MergeStrategy.MERGE_OVER */:
            return merge(transfer, initial);
        case "mergeUnder" /* MergeStrategy.MERGE_UNDER */:
            return merge(initial, transfer);
        default:
            throw Error('Invalid merge strategy must be one of MergeStrategy');
    }
}

function browserRehydrateReducer(
// eslint-disable-next-line @typescript-eslint/ban-types
platformId, _transferStateService, config) {
    const isBrowser = isPlatformBrowser(platformId);
    if (isBrowser) {
        /**
         * This will grab from the transferred state all of the state keys created by this library for the root
         * and any features that were added
         */
        const statesTransferred = _transferStateService.get(REHYDRATE_TRANSFER_STATE, undefined);
        /**
         * Only return a reducer that will attempt to rehydrate the state if there were states transferred to begin with
         */
        if (statesTransferred) {
            return (reducer) => (state, action) => {
                if (action.type === INIT) {
                    const merged = mergeStates(state, statesTransferred, config.mergeStrategy) || {};
                    return reducer(merged, action);
                }
                return reducer(state, action);
            };
        }
    }
    /**
     * If the app is not in the browser or if there were no transferred states then return a meta reducer
     * that does not don anything to the state
     */
    return (reducer) => (state, action) => reducer(state, action);
}

class RehydrationLogger {
    constructor(config) {
        this.config = config;
    }
    log(msg) {
        if (!this.config.disableWarnings) {
            console.warn(`NgRx Universal Rehydration: ${msg}`);
        }
    }
}
RehydrationLogger.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: RehydrationLogger, deps: [{ token: REHYDRATE_ROOT_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
RehydrationLogger.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: RehydrationLogger });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: RehydrationLogger, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [REHYDRATE_ROOT_CONFIG]
                    }] }];
    } });

const REHYDRATE_STORE = 'rehydrateStore';
const initialRehydrateStore = {
    slices: [],
};
const addSlice = createAction('[@trellisorg/ngrx-universal-rehydrate] add store slice(s)', props());
const rehydrateFeature = createFeature({
    name: REHYDRATE_STORE,
    reducer: createReducer(initialRehydrateStore, on(addSlice, (state, { slices }) => (Object.assign(Object.assign({}, state), { slices: [...new Set([...state.slices, ...slices])] })))),
});
const selectStateToTransfer = createSelector((state) => state, rehydrateFeature.selectSlices, (state, slices) => slices.length > 0
    ? slices.reduce((toTransfer, slice) => (Object.assign(Object.assign({}, toTransfer), { [slice]: state[slice] })), {})
    : undefined);
class RehydrateStoreModule {
}
RehydrateStoreModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: RehydrateStoreModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
RehydrateStoreModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.0.0", ngImport: i0, type: RehydrateStoreModule, imports: [i1.StoreFeatureModule] });
RehydrateStoreModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: RehydrateStoreModule, imports: [StoreModule.forFeature(rehydrateFeature)] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: RehydrateStoreModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [StoreModule.forFeature(rehydrateFeature)],
                }]
        }] });

class NgrxUniversalRehydrateBrowserRootModule {
    constructor(_injector, rootConfig, 
    // eslint-disable-next-line @typescript-eslint/ban-types
    platformId, _store) {
        var _a;
        /*
         * If we are on the server then we need to add the slices defined at root
         * to the store so they can be transferred
         */
        if (isPlatformServer(platformId)) {
            _store.dispatch(addSlice({ slices: (_a = rootConfig.stores) !== null && _a !== void 0 ? _a : [] }));
        }
    }
}
NgrxUniversalRehydrateBrowserRootModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: NgrxUniversalRehydrateBrowserRootModule, deps: [{ token: i0.Injector }, { token: REHYDRATE_ROOT_CONFIG }, { token: PLATFORM_ID }, { token: i1.Store }], target: i0.ɵɵFactoryTarget.NgModule });
NgrxUniversalRehydrateBrowserRootModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.0.0", ngImport: i0, type: NgrxUniversalRehydrateBrowserRootModule, imports: [RehydrateStoreModule] });
NgrxUniversalRehydrateBrowserRootModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: NgrxUniversalRehydrateBrowserRootModule, imports: [RehydrateStoreModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: NgrxUniversalRehydrateBrowserRootModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [RehydrateStoreModule],
                }]
        }], ctorParameters: function () {
        return [{ type: i0.Injector }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [REHYDRATE_ROOT_CONFIG]
                    }] }, { type: Object, decorators: [{
                        type: Inject,
                        args: [PLATFORM_ID]
                    }] }, { type: i1.Store }];
    } });
class NgrxUniversalRehydrateBrowserFeatureModule {
    constructor(stores, 
    // eslint-disable-next-line @typescript-eslint/ban-types
    platformId, _store) {
        this.stores = stores;
        this._store = _store;
        if (isPlatformServer(platformId))
            this._store.dispatch(addSlice({ slices: stores }));
    }
}
NgrxUniversalRehydrateBrowserFeatureModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: NgrxUniversalRehydrateBrowserFeatureModule, deps: [{ token: FEATURE_STORES }, { token: PLATFORM_ID }, { token: i1.Store }], target: i0.ɵɵFactoryTarget.NgModule });
NgrxUniversalRehydrateBrowserFeatureModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.0.0", ngImport: i0, type: NgrxUniversalRehydrateBrowserFeatureModule });
NgrxUniversalRehydrateBrowserFeatureModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: NgrxUniversalRehydrateBrowserFeatureModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: NgrxUniversalRehydrateBrowserFeatureModule, decorators: [{
            type: NgModule,
            args: [{}]
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [FEATURE_STORES]
                    }] }, { type: Object, decorators: [{
                        type: Inject,
                        args: [PLATFORM_ID]
                    }] }, { type: i1.Store }];
    } });
class NgrxUniversalRehydrateBrowserModule {
    static forRoot(config) {
        return {
            ngModule: NgrxUniversalRehydrateBrowserRootModule,
            providers: [
                {
                    provide: REHYDRATE_ROOT_CONFIG,
                    useValue: Object.assign(Object.assign({}, defaultRehydrationRootConfig), config),
                },
                {
                    provide: META_REDUCERS,
                    deps: [PLATFORM_ID, TransferState, REHYDRATE_ROOT_CONFIG],
                    useFactory: browserRehydrateReducer,
                    multi: true,
                },
                RehydrationLogger,
            ],
        };
    }
    static forFeature(stores) {
        return {
            ngModule: NgrxUniversalRehydrateBrowserFeatureModule,
            providers: [
                {
                    provide: FEATURE_STORES,
                    useValue: stores,
                    multi: true,
                },
            ],
        };
    }
}
NgrxUniversalRehydrateBrowserModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: NgrxUniversalRehydrateBrowserModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
NgrxUniversalRehydrateBrowserModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.0.0", ngImport: i0, type: NgrxUniversalRehydrateBrowserModule });
NgrxUniversalRehydrateBrowserModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: NgrxUniversalRehydrateBrowserModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: NgrxUniversalRehydrateBrowserModule, decorators: [{
            type: NgModule,
            args: [{}]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { NgrxUniversalRehydrateBrowserFeatureModule, NgrxUniversalRehydrateBrowserModule, NgrxUniversalRehydrateBrowserRootModule, REHYDRATE_TRANSFER_STATE, selectStateToTransfer };
